import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chip } from '@nyce/admin-kit/ui';
import type { Tone } from '../../../components/status';
import { useAsync } from '../../../components/common';
import { monitorsFor, type MonitorRun } from '../../api/monitors';
import './monitors.css';

// Small building blocks shared by the monitor list, detail and dialogs.

/** The view lives in the URL query (shareable): `get(key)` and `set({key: value | null})`, replacing history. */
export function useQueryState() {
  const [params, setParams] = useSearchParams();
  const set = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (cur) => {
          const next = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(patch))
            if (v == null || v === '') next.delete(k);
            else next.set(k, v);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );
  return { get: (key: string) => params.get(key) ?? '', set };
}

/** Shared lookups of the monitor pages: the environment's tenants and systems (best effort: an empty list on error). */
export function useLookups(target: string) {
  const systems = useAsync(() => monitorsFor(target).systems(), [target]);
  const tenants = useAsync(() => monitorsFor(target).tenants(), [target]);
  return { systems: systems.data ?? [], tenants: tenants.data ?? [], error: systems.error };
}

const RUN_TONE: Record<string, Tone | 'blue'> = {
  success: 'green',
  no_rows: 'green',
  failed: 'red',
  running: 'blue',
  skipped: 'grey',
};
const SEVERITY_TONE: Record<string, Tone | 'blue'> = { critical: 'red', warning: 'amber', info: 'blue' };
const ALERT_TONE: Record<string, Tone | 'blue'> = { open: 'amber', resolved: 'green' };
const MAIL_TONE: Record<string, Tone | 'blue'> = { sent: 'green', mocked: 'grey', failed: 'red' };
const KIND = { run: RUN_TONE, severity: SEVERITY_TONE, alert: ALERT_TONE, mail: MAIL_TONE };

/** A status word in its tone (text always shown, never colour alone). */
export function ToneChip({ kind, value }: { kind: keyof typeof KIND; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <Chip size="sm" className={`nyk-tone-${KIND[kind][value] ?? 'grey'}`} data-tone={kind}>
      {value.replace('_', ' ')}
    </Chip>
  );
}

export const runSummary = (r: MonitorRun) =>
  `${r.triggerRowCount ?? '—'} rows · ${r.newAlertCount ?? 0} new · ${r.stillOpenCount ?? 0} open · ${
    r.autoClosedCount ?? 0
  } closed`;

export const cell = (v: unknown): string =>
  v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);

/** Rows as a table; columns from `columns` or the union of the row keys. */
export function RowsTable({
  rows,
  columns,
  testId,
}: {
  rows: Record<string, unknown>[];
  columns?: string[];
  testId?: string;
}) {
  const cols = columns?.length ? columns : [...new Set(rows.flatMap((r) => Object.keys(r)))];
  return (
    <div className="nyk-scroll bm-rows">
      <table className="nyk-ui-table" data-testid={testId}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c}>{cell(r[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A trigger or row snapshot: array of objects → table, object → key/value, anything else → JSON. */
export function SnapshotView({ value }: { value: unknown }) {
  if (
    Array.isArray(value) &&
    value.length &&
    value.every((r) => r && typeof r === 'object' && !Array.isArray(r))
  )
    return <RowsTable rows={value as Record<string, unknown>[]} testId="snapshot-table" />;
  if (value && typeof value === 'object' && !Array.isArray(value))
    return (
      <div className="nyk-scroll">
        <table className="nyk-ui-table nyk-kv" data-testid="snapshot-kv">
          <tbody>
            {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
              <tr key={k}>
                <th>{k}</th>
                <td>{cell(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  return <pre className="bm-pre">{value == null ? '—' : JSON.stringify(value, null, 2)}</pre>;
}
